import { useEffect, useMemo, useState } from "react";
import {
  Download,
  FileBarChart2,
  Globe2,
  Info,
  Save,
  Ship,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  generateComplianceReport,
  loadReportingSetup,
  saveReportingProfile,
  saveTradeComplianceDetail,
} from "@/lib/api";
import type { Organisation } from "@/lib/types";
import { titleCase } from "@/lib/utils";

type Cadence =
  | "monthly"
  | "quarterly"
  | "half_yearly"
  | "calendar_year"
  | "financial_year"
  | "custom";
type ReportRow = Record<string, any>;
type TradeForm = Record<string, string>;

const MONEY_FIELDS = [
  "assessableValue",
  "freightAmount",
  "insuranceAmount",
  "customsDutyAmount",
  "importTaxAmount",
] as const;

function iso(date: Date) {
  return date.toISOString().slice(0, 10);
}

function endOfMonth(date: Date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0));
}

function periodBounds(cadence: Cadence, anchor: string, fiscalStart: number) {
  const date = new Date(`${anchor}T00:00:00Z`);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  let start: Date;
  let end: Date;
  if (cadence === "monthly") {
    start = new Date(Date.UTC(year, month, 1));
    end = endOfMonth(start);
  } else if (cadence === "quarterly") {
    const quarter = Math.floor(month / 3) * 3;
    start = new Date(Date.UTC(year, quarter, 1));
    end = new Date(Date.UTC(year, quarter + 3, 0));
  } else if (cadence === "half_yearly") {
    const half = month < 6 ? 0 : 6;
    start = new Date(Date.UTC(year, half, 1));
    end = new Date(Date.UTC(year, half + 6, 0));
  } else if (cadence === "calendar_year") {
    start = new Date(Date.UTC(year, 0, 1));
    end = new Date(Date.UTC(year, 11, 31));
  } else if (cadence === "financial_year") {
    const startMonth = fiscalStart - 1;
    const startYear = month < startMonth ? year - 1 : year;
    start = new Date(Date.UTC(startYear, startMonth, 1));
    end = new Date(Date.UTC(startYear + 1, startMonth, 0));
  } else {
    start = date;
    end = date;
  }
  return { start: iso(start), end: iso(end) };
}

function amount(value: unknown, currency = "INR") {
  if (value === null || value === undefined || value === "")
    return "NOT AVAILABLE";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

function download(name: string, content: string, type: string) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}

function csvCell(value: unknown) {
  const stringValue = value == null ? "" : String(value);
  return `"${stringValue.replace(/"/g, '""')}"`;
}

function hasTradeEvidence(row: ReportRow) {
  return Boolean(
    row.incoterm ||
      row.country_of_origin ||
      row.country_of_destination ||
      row.customs_document_type ||
      row.customs_document_number ||
      row.customs_document_date ||
      row.hs_code_summary ||
      row.export_reference,
  );
}

function recordedValue(value: unknown) {
  return value === null || value === undefined || value === ""
    ? ""
    : String(value);
}

export function ReportingCenter({
  organisation,
  canFinance,
}: {
  organisation: Organisation;
  canFinance: boolean;
}) {
  const [setup, setSetup] = useState<any>();
  const [report, setReport] = useState<any>();
  const [error, setError] = useState("");
  const [tradeError, setTradeError] = useState("");
  const [busy, setBusy] = useState(false);
  const [country, setCountry] = useState("IN");
  const [currency, setCurrency] = useState("INR");
  const [taxId, setTaxId] = useState(organisation.gstin || "");
  const [fiscalStart, setFiscalStart] = useState(4);
  const [cadence, setCadence] = useState<Cadence>("monthly");
  const [anchor, setAnchor] = useState(iso(new Date()));
  const [customStart, setCustomStart] = useState(
    iso(
      new Date(
        Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1),
      ),
    ),
  );
  const [customEnd, setCustomEnd] = useState(iso(new Date()));
  const [tradeRow, setTradeRow] = useState<ReportRow | null>(null);
  const [trade, setTrade] = useState<TradeForm>({ direction: "" });

  useEffect(() => {
    loadReportingSetup(organisation.id)
      .then((value) => {
        setSetup(value);
        if (value.profile) {
          setCountry(value.profile.country_code);
          setCurrency(value.profile.reporting_currency);
          setTaxId(value.profile.tax_registration_number || "");
          setFiscalStart(value.profile.fiscal_year_start_month);
        }
      })
      .catch((reason) => setError(reason.message));
  }, [organisation.id]);

  const bounds = useMemo(
    () =>
      cadence === "custom"
        ? { start: customStart, end: customEnd }
        : periodBounds(cadence, anchor, fiscalStart),
    [cadence, anchor, customStart, customEnd, fiscalStart],
  );
  const selectedRule = setup?.rules?.find(
    (rule: any) => rule.country_code === country,
  );

  async function run() {
    setBusy(true);
    setError("");
    try {
      setReport(
        await generateComplianceReport(
          organisation.id,
          country,
          bounds.start,
          bounds.end,
          cadence,
        ),
      );
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function saveProfile() {
    setBusy(true);
    setError("");
    try {
      await saveReportingProfile(organisation.id, {
        countryCode: country,
        taxRegistrationNumber: taxId,
        fiscalYearStartMonth: fiscalStart,
        reportingCurrency: currency,
      });
      await run();
    } catch (reason) {
      setError((reason as Error).message);
      setBusy(false);
    }
  }

  async function saveTrade() {
    if (!tradeRow) return;
    if (!trade.direction) {
      setTradeError(
        "Select whether this purchase is domestic, imported or exported.",
      );
      return;
    }
    const missingMoney = MONEY_FIELDS.find((field) => trade[field] === "");
    if (missingMoney) {
      setTradeError(
        "Confirm every monetary field. Enter 0 only when the recorded amount is genuinely zero or not applicable.",
      );
      return;
    }
    setBusy(true);
    setError("");
    setTradeError("");
    try {
      await saveTradeComplianceDetail(organisation.id, {
        ...trade,
        purchaseOrderId: tradeRow.purchase_order_id,
        invoiceId: tradeRow.invoice_id,
      });
      setTradeRow(null);
      await run();
    } catch (reason) {
      setTradeError((reason as Error).message);
      setBusy(false);
    }
  }

  function exportJson() {
    download(
      `raqoz-${country}-${bounds.start}-${bounds.end}.json`,
      JSON.stringify(report, null, 2),
      "application/json",
    );
  }

  function exportCsv() {
    const columns = [
      "invoice_no",
      "invoice_date",
      "invoice_status",
      "po_no",
      "vendor_name",
      "vendor_country",
      "vendor_tax_identifier",
      "currency",
      "taxable_amount",
      "tax_amount",
      "gross_amount",
      "payment_state",
      "reconciliation_status",
      "trade_direction",
      "incoterm",
      "country_of_origin",
      "country_of_destination",
      "customs_document_number",
      "hs_code_summary",
      "customs_duty_amount",
      "import_tax_amount",
    ];
    const rows = [
      columns.map(csvCell).join(","),
      ...(report?.rows || []).map((row: ReportRow) =>
        columns.map((column) => csvCell(row[column])).join(","),
      ),
    ];
    download(
      `raqoz-${country}-purchase-register-${bounds.start}-${bounds.end}.csv`,
      `\ufeff${rows.join("\n")}`,
      "text/csv;charset=utf-8",
    );
  }

  function openTrade(row: ReportRow) {
    const recorded = hasTradeEvidence(row);
    setTradeError("");
    setTradeRow(row);
    setTrade({
      direction: recorded ? row.trade_direction || "" : "",
      incoterm: recordedValue(row.incoterm),
      countryOfOrigin: recordedValue(row.country_of_origin),
      countryOfDestination: recordedValue(row.country_of_destination),
      customsDocumentType: recordedValue(row.customs_document_type),
      customsDocumentNumber: recordedValue(row.customs_document_number),
      customsDocumentDate: recordedValue(row.customs_document_date),
      hsCodeSummary: recordedValue(row.hs_code_summary),
      assessableValue: recorded ? recordedValue(row.assessable_value) : "",
      freightAmount: recorded ? recordedValue(row.freight_amount) : "",
      insuranceAmount: recorded ? recordedValue(row.insurance_amount) : "",
      customsDutyAmount: recorded ? recordedValue(row.customs_duty_amount) : "",
      importTaxAmount: recorded ? recordedValue(row.import_tax_amount) : "",
      exportReference: recordedValue(row.export_reference),
      notes: "",
    });
  }

  const summary = report?.summary || {};
  const rows: ReportRow[] = report?.rows || [];
  const hasPaymentEvidence = rows.some(
    (row) => row.payment_state && row.payment_state !== "not_created",
  );
  const summaryCards = [
    ["Invoices", report ? summary.invoice_count : "NOT AVAILABLE"],
    [
      "Taxable",
      rows.length ? amount(summary.taxable_amount, currency) : "NOT AVAILABLE",
    ],
    [
      "Input tax",
      rows.length ? amount(summary.input_tax, currency) : "NOT AVAILABLE",
    ],
    [
      "Paid",
      hasPaymentEvidence
        ? amount(summary.paid_amount, currency)
        : "NOT AVAILABLE",
    ],
    ["Imports", report ? summary.import_count : "NOT AVAILABLE"],
    [
      "Exceptions",
      report
        ? Number(summary.missing_vendor_tax_ids || 0) +
          Number(summary.missing_customs_documents || 0)
        : "NOT AVAILABLE",
    ],
  ];

  return (
    <div className="page-stack">
      <header className="page-heading">
        <div>
          <p className="eyebrow">COUNTRY-AWARE CONTROLS</p>
          <h1>Reporting & reconciliation</h1>
          <p>
            Generate period-locked purchase, tax, payment and cross-border
            evidence packs. These are operational aids, not statutory filing
            advice or proof of tax-authority reconciliation.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" disabled={!report} onClick={exportCsv}>
            <Download className="size-4" /> CSV
          </Button>
          <Button disabled={!report} onClick={exportJson}>
            <ShieldCheck className="size-4" /> Evidence pack
          </Button>
        </div>
      </header>

      {error && (
        <div className="error-panel" role="alert">
          {error}
        </div>
      )}

      <section className="boundary-panel" aria-labelledby="report-boundary">
        <Info className="size-5" aria-hidden="true" />
        <div>
          <h2 id="report-boundary">Reporting boundary</h2>
          <p>
            GST, VAT and country packs reconcile RAQOZ-recorded procurement
            evidence. They do not confirm government portal, customs, bank or
            statutory filing data unless a governed integration supplies it.
          </p>
        </div>
      </section>

      <section className="surface grid gap-6 p-5 xl:grid-cols-[1.1fr_1fr]">
        <div>
          <div className="flex items-center gap-3">
            <span className="section-heading__icon">
              <Globe2 className="size-4" />
            </span>
            <div>
              <h2 className="text-lg font-semibold">Reporting profile</h2>
              <p className="text-sm text-[var(--text-secondary)]">
                Organisation-level country, tax and financial-year context.
              </p>
            </div>
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Label>
              Country
              <Select
                value={country}
                onChange={(event) => setCountry(event.target.value)}
              >
                {(setup?.rules || []).map((rule: any) => (
                  <option key={rule.country_code} value={rule.country_code}>
                    {rule.country_name} · {rule.tax_system}
                  </option>
                ))}
              </Select>
            </Label>
            <Label>
              Reporting currency
              <Input
                maxLength={3}
                value={currency}
                onChange={(event) =>
                  setCurrency(event.target.value.toUpperCase())
                }
              />
            </Label>
            <Label>
              {selectedRule?.tax_identifier_label || "Tax registration number"}
              <Input
                value={taxId}
                onChange={(event) => setTaxId(event.target.value)}
              />
            </Label>
            <Label>
              Financial year starts
              <Select
                value={fiscalStart}
                onChange={(event) => setFiscalStart(Number(event.target.value))}
              >
                {Array.from({ length: 12 }, (_, index) => (
                  <option key={index + 1} value={index + 1}>
                    {new Date(2024, index, 1).toLocaleString(undefined, {
                      month: "long",
                    })}
                  </option>
                ))}
              </Select>
            </Label>
          </div>
          {canFinance && (
            <Button className="mt-5" disabled={busy} onClick={saveProfile}>
              <Save className="size-4" /> Save profile
            </Button>
          )}
        </div>
        <div className="surface-subtle p-5">
          <p className="eyebrow">CONTROL CHECKLIST</p>
          <ul className="mt-4 grid gap-2 text-sm text-[var(--text-secondary)]">
            {(selectedRule?.requirements || []).map((item: string) => (
              <li className="flex gap-2" key={item}>
                <span
                  className="mt-1.5 size-2 shrink-0 rounded-full bg-[var(--warning)]"
                  aria-hidden="true"
                />
                {item}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs leading-5 text-[var(--text-secondary)]">
            {selectedRule?.disclaimer ||
              "Select a reporting country to view its configured controls."}
          </p>
        </div>
      </section>

      <section className="surface p-5">
        <div className="grid items-end gap-4 md:grid-cols-3 xl:grid-cols-6">
          <Label>
            Period
            <Select
              value={cadence}
              onChange={(event) => setCadence(event.target.value as Cadence)}
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="half_yearly">Half-yearly</option>
              <option value="calendar_year">Calendar year</option>
              <option value="financial_year">Financial year</option>
              <option value="custom">Custom</option>
            </Select>
          </Label>
          {cadence !== "custom" ? (
            <Label>
              Anchor date
              <Input
                type="date"
                value={anchor}
                onChange={(event) => setAnchor(event.target.value)}
              />
            </Label>
          ) : (
            <>
              <Label>
                From
                <Input
                  type="date"
                  value={customStart}
                  onChange={(event) => setCustomStart(event.target.value)}
                />
              </Label>
              <Label>
                To
                <Input
                  type="date"
                  value={customEnd}
                  onChange={(event) => setCustomEnd(event.target.value)}
                />
              </Label>
            </>
          )}
          <div className="xl:col-span-2">
            <p className="text-xs font-semibold text-[var(--text-secondary)]">
              Report window
            </p>
            <p className="mt-2 text-sm font-semibold tabular-nums">
              {bounds.start} → {bounds.end}
            </p>
          </div>
          <Button disabled={busy} onClick={run}>
            <FileBarChart2 className="size-4" />
            {busy ? "Generating…" : "Generate report"}
          </Button>
        </div>
      </section>

      {report && (
        <>
          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
            {summaryCards.map(([label, value]) => (
              <div className="metric-card" key={String(label)}>
                <p>{label}</p>
                <strong
                  className={value === "NOT AVAILABLE" ? "unavailable" : ""}
                >
                  {value}
                </strong>
              </div>
            ))}
          </section>

          <section className="surface overflow-hidden">
            <header className="section-heading">
              <span className="section-heading__icon">
                <FileBarChart2 className="size-4" />
              </span>
              <div>
                <h2>Reconciliation register</h2>
                <p>
                  {rows.length} invoice records · snapshot{" "}
                  {report.report_run_id ? "saved" : "view only"}
                </p>
              </div>
            </header>
            <div className="compact-table-wrap max-h-[680px]">
              <table className="compact-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Supplier</th>
                    <th className="numeric">Tax</th>
                    <th>Payment</th>
                    <th>Reconciliation</th>
                    <th>Trade evidence</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const recordedTrade = hasTradeEvidence(row);
                    return (
                      <tr key={row.invoice_id}>
                        <td>
                          <strong>{row.invoice_no}</strong>
                          <span className="table-subline">
                            {row.invoice_date} · {row.po_no}
                          </span>
                        </td>
                        <td>
                          {row.vendor_name}
                          <span className="table-subline">
                            {row.vendor_tax_identifier || "Tax ID missing"}
                          </span>
                        </td>
                        <td className="numeric">
                          {amount(row.tax_amount, row.currency)}
                          <span className="table-subline">
                            Gross {amount(row.gross_amount, row.currency)}
                          </span>
                        </td>
                        <td>
                          {row.payment_state === "not_created"
                            ? "NOT AVAILABLE"
                            : titleCase(row.payment_state)}
                        </td>
                        <td>
                          {row.payment_state === "not_created"
                            ? "NOT AVAILABLE"
                            : titleCase(row.reconciliation_status)}
                        </td>
                        <td>
                          {recordedTrade
                            ? titleCase(row.trade_direction)
                            : "NOT AVAILABLE"}
                          <span className="table-subline">
                            {recordedTrade
                              ? row.customs_document_number ||
                                (row.trade_direction === "domestic"
                                  ? "—"
                                  : "Document missing")
                              : "No trade evidence recorded"}
                          </span>
                        </td>
                        <td>
                          {canFinance && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openTrade(row)}
                              aria-label={`Review trade evidence for invoice ${row.invoice_no}`}
                            >
                              <Ship className="size-4" /> Review
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {!rows.length && (
              <div className="empty-state">
                <FileBarChart2 className="size-5" />
                <h3>No invoices in this period</h3>
                <p>Change the reporting window or generate another period.</p>
              </div>
            )}
          </section>
        </>
      )}

      <Dialog
        open={Boolean(tradeRow)}
        onOpenChange={(open) => {
          if (!open) setTradeRow(null);
        }}
      >
        <DialogContent className="w-[min(980px,calc(100vw-2rem))]">
          <DialogHeader
            eyebrow="CROSS-BORDER EVIDENCE"
            title={
              tradeRow
                ? `${tradeRow.invoice_no} · ${tradeRow.po_no}`
                : "Trade evidence"
            }
            description="Record known import or export evidence. Blank commercial amounts cannot be silently converted to zero."
          />
          <div className="grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4">
            <Label>
              Direction <span aria-hidden="true">*</span>
              <Select
                required
                value={trade.direction || ""}
                onChange={(event) =>
                  setTrade({ ...trade, direction: event.target.value })
                }
              >
                <option value="">Select direction</option>
                <option value="domestic">Domestic</option>
                <option value="import">Import</option>
                <option value="export">Export</option>
              </Select>
            </Label>
            <Label>
              Incoterm
              <Input
                maxLength={3}
                placeholder="CIF"
                value={trade.incoterm || ""}
                onChange={(event) =>
                  setTrade({
                    ...trade,
                    incoterm: event.target.value.toUpperCase(),
                  })
                }
              />
            </Label>
            <Label>
              Country of origin
              <Input
                maxLength={2}
                placeholder="DE"
                value={trade.countryOfOrigin || ""}
                onChange={(event) =>
                  setTrade({
                    ...trade,
                    countryOfOrigin: event.target.value.toUpperCase(),
                  })
                }
              />
            </Label>
            <Label>
              Destination
              <Input
                maxLength={2}
                placeholder="IN"
                value={trade.countryOfDestination || ""}
                onChange={(event) =>
                  setTrade({
                    ...trade,
                    countryOfDestination: event.target.value.toUpperCase(),
                  })
                }
              />
            </Label>
            <Label>
              Customs document type
              <Input
                placeholder="Bill of Entry"
                value={trade.customsDocumentType || ""}
                onChange={(event) =>
                  setTrade({
                    ...trade,
                    customsDocumentType: event.target.value,
                  })
                }
              />
            </Label>
            <Label>
              Document number
              <Input
                value={trade.customsDocumentNumber || ""}
                onChange={(event) =>
                  setTrade({
                    ...trade,
                    customsDocumentNumber: event.target.value,
                  })
                }
              />
            </Label>
            <Label>
              Document date
              <Input
                type="date"
                value={trade.customsDocumentDate || ""}
                onChange={(event) =>
                  setTrade({
                    ...trade,
                    customsDocumentDate: event.target.value,
                  })
                }
              />
            </Label>
            <Label>
              HS code summary
              <Input
                value={trade.hsCodeSummary || ""}
                onChange={(event) =>
                  setTrade({ ...trade, hsCodeSummary: event.target.value })
                }
              />
            </Label>
            {[
              ["assessableValue", "Assessable value"],
              ["freightAmount", "Freight"],
              ["insuranceAmount", "Insurance"],
              ["customsDutyAmount", "Customs duty"],
              ["importTaxAmount", "Import tax / IGST"],
            ].map(([field, label]) => (
              <Label key={field}>
                {label} <span aria-hidden="true">*</span>
                <Input
                  required
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Confirm amount"
                  value={trade[field] || ""}
                  onChange={(event) =>
                    setTrade({ ...trade, [field]: event.target.value })
                  }
                />
              </Label>
            ))}
            <Label>
              Export reference
              <Input
                value={trade.exportReference || ""}
                onChange={(event) =>
                  setTrade({ ...trade, exportReference: event.target.value })
                }
              />
            </Label>
            <Label className="sm:col-span-2">
              Notes
              <Textarea
                value={trade.notes || ""}
                onChange={(event) =>
                  setTrade({ ...trade, notes: event.target.value })
                }
              />
            </Label>
          </div>
          <div className="border-t border-[var(--border)] bg-[var(--surface-secondary)] p-5">
            <p className="mb-4 text-xs text-[var(--text-secondary)]">
              Required monetary fields demand an explicit value. Enter 0 only
              after confirming that zero is the correct recorded amount.
            </p>
            {tradeError && (
              <p className="mb-4 text-sm text-[var(--error)]" role="alert">
                {tradeError}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setTradeRow(null)}>
                Cancel
              </Button>
              <Button disabled={busy} onClick={saveTrade}>
                <Save className="size-4" /> Save evidence
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
